import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Download, RotateCcw, Upload as UploadIcon, Sparkles, ShieldAlert, Unlock, Lock, Plus, Pencil, Lock as LockIcon, Archive as ArchiveIcon, Gavel, ClipboardCheck, CheckCircle2, AlertTriangle, FileWarning, Users as UsersIcon, FileSpreadsheet, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import DictationField from "@/components/DictationField";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { API_BASE, formatApiErrorDetail, describeApiError } from "../lib/api";
import { formatDate } from "../lib/dateFormat";
import { useAuth } from "../context/AuthContext";
import CopilotPanel from "../components/copilot/CopilotPanel";
import { StatusPill } from "../components/StatusPill";
import { HighRiskBadge } from "../components/HighRiskBadge";

const STATUSES = [
  "New",
  "In Progress",
  "Under Investigation",
  "Pending Follow-up",
  "Escalated",
  "Closed",
];
const DOC_CATEGORIES = [
  "Investigation Notes",
  "Witness Statements",
  "Emails",
  "Counseling Documents",
  "Policies",
  "Performance Documents",
  "Other",
];
const CASE_SOURCES = [
  "Manager Report",
  "Anonymous Complaint",
  "Hotline",
  "HR Observation",
  "Compliance Concern",
  "Employee Grievance",
];

function FieldRow({ label, children }) {
  return (
    <div className="grid grid-cols-3 gap-3 border-b border-[color:var(--border-subtle)]/70 py-2.5 last:border-b-0">
      <div className="text-xs font-medium uppercase tracking-wider text-[color:var(--page-fg-muted)]">{label}</div>
      <div className="col-span-2 text-sm text-[color:var(--page-fg)]">
        {children || <span className="text-[color:var(--page-fg-muted)]">—</span>}
      </div>
    </div>
  );
}

function ReopenDialog({ open, setOpen, onConfirm }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const handle = async () => {
    if (!reason.trim()) {
      toast.error("Reason is required to reopen a closed case");
      return;
    }
    setSaving(true);
    await onConfirm(reason);
    setSaving(false);
    setReason("");
    setOpen(false);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="rounded-2xl">
        <DialogHeader>
          <DialogTitle>Reopen case</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-[color:var(--page-fg-secondary)]">
          Reopening a closed case creates an audit entry and notifies Compliance.
          Please provide a reason.
        </p>
        <Textarea
          rows={4}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. New evidence provided by witness"
          data-testid="reopen-reason-textarea"
        />
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handle}
            disabled={saving}
            data-testid="reopen-confirm-button"
            className="bg-brand-teal text-white hover:bg-brand-tealDark"
          >
            {saving ? "Reopening…" : "Reopen case"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Phase 9 — HIPAA PHI override modal (HR admin only)
function PhiOverrideDialog({ open, setOpen, doc, onConfirm }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const handle = async () => {
    if (reason.trim().length < 10) {
      toast.error("Reason must be at least 10 characters");
      return;
    }
    setSaving(true);
    await onConfirm(reason.trim());
    setSaving(false);
    setReason("");
    setOpen(false);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="rounded-2xl" data-testid="phi-override-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Unlock className="h-4 w-4" style={{ color: "#E8CB8E" }} />
            Override PHI restriction
          </DialogTitle>
        </DialogHeader>
        <div
          className="rounded-xl px-3 py-2 text-xs"
          style={{
            backgroundColor: "rgba(232, 203, 142, 0.12)",
            border: "1px solid rgba(232, 203, 142, 0.4)",
            color: "#7A5C18",
          }}
        >
          <p className="font-medium">Compliance impact</p>
          <p className="mt-1">
            Removing the PHI flag on <b>{doc?.filename}</b> will allow Manager and
            Leadership roles to download this document. The override is logged
            with your user, IP, and reason — and is reviewed in the periodic
            HIPAA access review.
          </p>
        </div>
        <p className="text-sm text-[color:var(--page-fg-secondary)]">
          Please describe why this document should not be treated as PHI
          (false positive, redacted training material, etc.). Required, ≥ 10
          characters.
        </p>
        <Textarea
          rows={4}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. False positive — file contains a publicly-available training scenario, no real patient data."
          data-testid="phi-override-reason-textarea"
        />
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handle}
            disabled={saving}
            data-testid="phi-override-confirm-button"
            className="bg-brand-teal text-white hover:bg-brand-tealDark"
          >
            {saving ? "Applying…" : "Apply override"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// Phase 10 — Legal Hold dialog (HR Admin only)
function LegalHoldDialog({ open, setOpen, currentlyHeld, currentReason, onConfirm }) {
  const placing = !currentlyHeld;
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setReason(currentReason || "");
  }, [open, currentReason]);
  const handle = async () => {
    if (placing && reason.trim().length < 10) {
      toast.error("Reason must be at least 10 characters");
      return;
    }
    setSaving(true);
    await onConfirm(placing, reason.trim());
    setSaving(false);
    setOpen(false);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="rounded-2xl" data-testid="legal-hold-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gavel className="h-4 w-4" style={{ color: "#C03030" }} />
            {placing ? "Place case on legal hold" : "Release legal hold"}
          </DialogTitle>
        </DialogHeader>
        {placing ? (
          <div
            className="rounded-xl px-3 py-2 text-xs"
            style={{
              backgroundColor: "rgba(229,77,77,0.10)",
              border: "1px solid rgba(229,77,77,0.4)",
              color: "#7A1A1A",
            }}
          >
            <p className="font-semibold">Effects of placing the hold</p>
            <p className="mt-1">
              All edits are blocked (HTTP 423). New documents cannot be uploaded.
              The case is excluded from retention archive cycles. Only an HR admin
              can clear the hold.
            </p>
          </div>
        ) : (
          <p className="text-xs text-[color:var(--page-fg-secondary)]">
            Releasing the hold restores normal lifecycle (edits, archive eligibility).
            The release reason is optional but appears in the audit trail.
          </p>
        )}
        <Textarea
          rows={4}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={placing ? "Pending litigation: <case ref / counsel name>" : "Reason for release (optional)"}
          data-testid="legal-hold-reason-textarea"
        />
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handle}
            disabled={saving}
            data-testid="legal-hold-confirm-button"
            className={
              placing
                ? "bg-red-600 text-white hover:bg-red-700"
                : "bg-brand-teal text-white hover:bg-brand-tealDark"
            }
          >
            {saving ? "Working…" : placing ? "Place on hold" : "Release hold"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// Phase 10 — Witness add/edit dialog
function WitnessDialog({ open, setOpen, existing, onConfirm }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      setForm(
        existing
          ? {
              interviewee_name: existing.interviewee_name || "",
              interviewee_relationship: existing.interviewee_relationship || "",
              interview_date: (existing.interview_date || "").slice(0, 16),
              present_parties: (existing.present_parties || []).join(", "),
              summary: existing.summary || "",
              recorded: !!existing.recorded,
              confidential: existing.confidential !== false,
            }
          : {
              interviewee_name: "",
              interviewee_relationship: "",
              interview_date: new Date().toISOString().slice(0, 16),
              present_parties: "",
              summary: "",
              recorded: false,
              confidential: true,
            },
      );
    }
  }, [open, existing]);
  const handle = async () => {
    if (!form.interviewee_name?.trim()) {
      toast.error("Interviewee name is required");
      return;
    }
    if (!form.summary?.trim()) {
      toast.error("Interview summary is required");
      return;
    }
    setSaving(true);
    const payload = {
      interviewee_name: form.interviewee_name.trim(),
      interviewee_relationship: (form.interviewee_relationship || "").trim() || "—",
      interview_date: new Date(form.interview_date).toISOString(),
      present_parties: (form.present_parties || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
      summary: form.summary.trim(),
      recorded: !!form.recorded,
      confidential: !!form.confidential,
    };
    await onConfirm(payload);
    setSaving(false);
    setOpen(false);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-xl rounded-2xl" data-testid="witness-dialog">
        <DialogHeader>
          <DialogTitle>{existing ? "Edit witness interview" : "Add witness interview"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-1 sm:col-span-1">
            <Label className="text-xs text-[color:var(--page-fg-muted)]">Interviewee name *</Label>
            <Input
              data-testid="witness-name"
              value={form.interviewee_name || ""}
              onChange={(e) => setForm((s) => ({ ...s, interviewee_name: e.target.value }))}
            />
          </div>
          <div className="space-y-1 sm:col-span-1">
            <Label className="text-xs text-[color:var(--page-fg-muted)]">Relationship</Label>
            <Input
              data-testid="witness-relationship"
              value={form.interviewee_relationship || ""}
              onChange={(e) => setForm((s) => ({ ...s, interviewee_relationship: e.target.value }))}
              placeholder="co-worker / supervisor / patient family member"
            />
          </div>
          <div className="space-y-1 sm:col-span-1">
            <Label className="text-xs text-[color:var(--page-fg-muted)]">Interview date / time</Label>
            <Input
              data-testid="witness-date"
              type="datetime-local"
              value={form.interview_date || ""}
              onChange={(e) => setForm((s) => ({ ...s, interview_date: e.target.value }))}
            />
          </div>
          <div className="space-y-1 sm:col-span-1">
            <Label className="text-xs text-[color:var(--page-fg-muted)]">Present parties (comma-sep)</Label>
            <Input
              data-testid="witness-present-parties"
              value={form.present_parties || ""}
              onChange={(e) => setForm((s) => ({ ...s, present_parties: e.target.value }))}
              placeholder="Trish (HR), Bob (witness)"
            />
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label className="text-xs text-[color:var(--page-fg-muted)]">Summary *</Label>
            <DictationField
              rows={5}
              data-testid="witness-summary"
              testidPrefix="witness-summary"
              targetField="witness_statement"
              value={form.summary || ""}
              onChange={(e) => setForm((s) => ({ ...s, summary: e.target.value }))}
            />
          </div>
          <div className="flex items-center gap-2 sm:col-span-1">
            <input
              id="witness-recorded"
              data-testid="witness-recorded"
              type="checkbox"
              checked={!!form.recorded}
              onChange={(e) => setForm((s) => ({ ...s, recorded: e.target.checked }))}
            />
            <Label htmlFor="witness-recorded" className="text-sm">Recorded</Label>
          </div>
          <div className="flex items-center gap-2 sm:col-span-1">
            <input
              id="witness-confidential"
              data-testid="witness-confidential"
              type="checkbox"
              checked={!!form.confidential}
              onChange={(e) => setForm((s) => ({ ...s, confidential: e.target.checked }))}
            />
            <Label htmlFor="witness-confidential" className="text-sm">Confidential (redacted from non-Compliance exports)</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            data-testid="witness-save-button"
            onClick={handle}
            disabled={saving}
            className="bg-brand-teal text-white hover:bg-brand-tealDark"
          >
            {saving ? "Saving…" : existing ? "Save changes" : "Add interview"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}




// Phase 10 — Compliance Checklist panel rendered above CopilotPanel
function ComplianceChecklistPanel({ data, loading, onLoad }) {
  return (
    <div className="brand-card p-6 mb-4" data-testid="compliance-checklist-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-[color:var(--page-fg)] flex items-center gap-2">
            <ClipboardCheck className="h-4 w-4 text-[color:var(--page-fg)]" />
            Compliance checklist
          </h3>
          <p className="mt-1 text-xs text-[color:var(--page-fg-muted)]">
            Required documentation, missing investigation steps, suggested
            policy references and follow-ups for this case. Cached 30 minutes.
          </p>
        </div>
        <Button
          data-testid="compliance-checklist-load-button"
          onClick={onLoad}
          disabled={loading}
          variant="outline"
          size="sm"
          className="border-brand-teal text-[color:var(--page-fg)] hover:bg-[color:var(--surface-2)]"
        >
          <Sparkles className="mr-1 h-3.5 w-3.5" />
          {loading ? "Generating…" : data ? "Refresh" : "Generate"}
        </Button>
      </div>

      {!data ? (
        <p className="mt-4 text-sm text-[color:var(--page-fg-muted)]">
          Click <b>Generate</b> to compute compliance signals (rule-based + LLM policy suggestions).
        </p>
      ) : (
        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
          {/* Required docs */}
          <div data-testid="checklist-required-docs">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-[color:var(--page-fg-muted)]">
              Required documentation
            </h4>
            <ul className="mt-2 space-y-1.5">
              {(data.required_docs || []).map((d, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 rounded-lg px-2 py-1.5"
                  style={{
                    backgroundColor: d.present
                      ? "rgba(45, 156, 92, 0.08)"
                      : "rgba(232, 203, 142, 0.10)",
                  }}
                >
                  {d.present ? (
                    <CheckCircle2 className="mt-0.5 h-4 w-4 text-[color:var(--success-fg)]" />
                  ) : (
                    <FileWarning className="mt-0.5 h-4 w-4 text-[color:#9A7A2A] dark:text-[color:#E8CB8E]" />
                  )}
                  <div className="flex-1 text-xs text-[color:var(--page-fg-secondary)]">
                    <p>{d.label}</p>
                    <p className="text-[10px] text-[color:var(--page-fg-muted)]">
                      Category: {d.category}
                      {d.doc_filename ? ` · ${d.doc_filename}` : ""}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Missing steps */}
          <div data-testid="checklist-missing-steps">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-[color:var(--page-fg-muted)]">
              Missing investigation steps
            </h4>
            {(data.missing_steps || []).length === 0 ? (
              <p className="mt-2 rounded-lg bg-[color:var(--success-bg)] px-2 py-1.5 text-xs text-[color:var(--success-fg)]">
                No procedural gaps detected.
              </p>
            ) : (
              <ul className="mt-2 space-y-1.5">
                {data.missing_steps.map((s, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2 rounded-lg px-2 py-1.5"
                    style={{
                      backgroundColor:
                        s.severity === "high"
                          ? "rgba(229,77,77,0.10)"
                          : "rgba(232,203,142,0.10)",
                    }}
                  >
                    <AlertTriangle
                      className={`mt-0.5 h-4 w-4 ${
                        s.severity === "high" ? "text-[color:var(--danger-fg)]" : "text-[color:#9A7A2A] dark:text-[color:#E8CB8E]"
                      }`}
                    />
                    <div className="flex-1 text-xs text-[color:var(--page-fg-secondary)]">
                      <p className="font-medium">{s.step}</p>
                      <p className="text-[10px] text-[color:var(--page-fg-muted)]">{s.rationale}</p>
                    </div>
                    <span
                      className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase ${
                        s.severity === "high"
                          ? "bg-[color:var(--danger-bg)] text-[color:var(--danger-fg)]"
                          : "bg-[color:rgba(214,177,106,0.12)] text-[color:#9A7A2A] dark:text-[color:#E8CB8E]"
                      }`}
                    >
                      {s.severity}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Suggested policies */}
          <div data-testid="checklist-suggested-policies">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-[color:var(--page-fg-muted)]">
              Suggested policies
            </h4>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {(data.suggested_policies || []).map((p, i) => (
                <span
                  key={i}
                  className="rounded-full bg-[color:var(--surface-2)] px-2.5 py-1 text-xs text-brand-tealDark"
                >
                  {p}
                </span>
              ))}
            </div>
          </div>

          {/* Suggested follow-ups */}
          <div data-testid="checklist-suggested-followups">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-[color:var(--page-fg-muted)]">
              Suggested follow-ups
            </h4>
            {(data.suggested_followups || []).length === 0 ? (
              <p className="mt-2 text-xs text-[color:var(--page-fg-muted)]">
                No automatic follow-ups for this state.
              </p>
            ) : (
              <ul className="mt-2 list-inside list-disc space-y-1 text-xs text-[color:var(--page-fg-secondary)]">
                {data.suggested_followups.map((f, i) => (
                  <li key={i}>{f}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {data && (
        <p className="mt-3 text-[10px] text-[color:var(--page-fg-muted)]">
          Generated {new Date(data.generated_at).toLocaleString()} · model {data.model || "rule-based"}
        </p>
      )}
    </div>
  );
}



export default function CaseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [caseData, setCaseData] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [taxonomy, setTaxonomy] = useState({});
  const [hrPartners, setHrPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadCategory, setUploadCategory] = useState("Investigation Notes");
  const [uploadCategoryConfidence, setUploadCategoryConfidence] = useState(null);
  const [uploadReplacesId, setUploadReplacesId] = useState("");
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  // Phase 5
  const [related, setRelated] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [reopenOpen, setReopenOpen] = useState(false);
  // Phase 9 — PHI override modal
  const [overrideDoc, setOverrideDoc] = useState(null);
  // Phase 10 — witnesses + compliance checklist + legal hold modal state
  const [witnesses, setWitnesses] = useState([]);
  const [witnessOpen, setWitnessOpen] = useState(false);
  const [editingWitness, setEditingWitness] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [checklistLoading, setChecklistLoading] = useState(false);
  const [legalHoldOpen, setLegalHoldOpen] = useState(false);

  // Phase 2 (May 2026) — admin-initiated archive / restore (soft-delete)
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiveReason, setArchiveReason] = useState("");
  const [archiveConfirmText, setArchiveConfirmText] = useState("");
  const [archiveSubmitting, setArchiveSubmitting] = useState(false);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [restoreReason, setRestoreReason] = useState("");
  const [restoreSubmitting, setRestoreSubmitting] = useState(false);
  // Phase 2 — hard delete
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleteReason, setDeleteReason] = useState("");
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  const canEdit = user?.role === "hr" || user?.role === "manager";
  const canUpload = canEdit;
  const canSeeAudit = user?.role === "hr" || user?.role === "compliance" || user?.role === "legal_review";
  const isHR = user?.role === "hr";
  const isHRAdmin = isHR && !!user?.is_admin;
  // Phase 9 — only HR admins may override the PHI flag
  const canOverridePhi = isHRAdmin;
  // Phase 7b / Phase 10 — Co-pilot tab visibility + write gate
  const canSeeCopilot =
    user?.role === "hr" || user?.role === "compliance" || user?.role === "legal_review";
  const copilotReadOnly = user?.role === "compliance" || user?.role === "legal_review";
  // Phase 10 — legal hold gating
  const onLegalHold = !!caseData?.legal_hold;
  const archived = !!caseData?.archived;
  const writesBlocked = onLegalHold || archived;
  const [draftingClosure, setDraftingClosure] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [caseRes, docsRes, taxRes, partnersRes] = await Promise.all([
        api.get(`/cases/${id}`),
        api.get(`/cases/${id}/documents`),
        api.get("/config/issue-taxonomy").catch(() => ({ data: { taxonomy: {} } })),
        api.get("/config/hr-partners").catch(() => ({ data: [] })),
      ]);
      setCaseData(caseRes.data);
      setDocuments(docsRes.data);
      setTaxonomy(taxRes.data?.taxonomy || {});
      setHrPartners(partnersRes.data || []);
      setEditForm({
        status: caseRes.data.status,
        source: caseRes.data.source || "Manager Report",
        issue_type: caseRes.data.issue_type,
        specific_issue: caseRes.data.specific_issue,
        case_details: caseRes.data.case_details,
        outcome: caseRes.data.outcome || "",
        closure_summary: caseRes.data.closure_summary || "",
        follow_up_required: caseRes.data.follow_up_required,
        follow_up_date: caseRes.data.follow_up_date?.slice(0, 10) || "",
        follow_up_notes: caseRes.data.follow_up_notes || "",
        follow_up_completed: !!caseRes.data.follow_up_completed,
        investigation_start_date: caseRes.data.investigation_start_date?.slice(0, 10) || "",
        investigation_end_date: caseRes.data.investigation_end_date?.slice(0, 10) || "",
        // Phase 2 (Feb 2026) — HR discussion date. Empty string maps to null
        // at submit time so the backend clears the field cleanly.
        date_of_discussion: caseRes.data.date_of_discussion?.slice(0, 10) || "",
        // Phase 2 (Feb 2026 — post-import editability) — HR can correct the
        // original intake fields after case creation or CSV import.
        date_reported: caseRes.data.date_reported?.slice(0, 10) || "",
        reported_by: caseRes.data.reported_by || "",
        assigned_hr_partner_id: caseRes.data.assigned_hr_partner_id || "",
      });
      if (canSeeAudit) {
        const al = await api.get("/audit-logs", {
          params: { entity_type: "case", entity_id: id, limit: 100 },
        });
        setAuditLogs(al.data);
      }
      // Phase 5 — related + timeline (independent, fire-and-forget)
      api.get(`/cases/${id}/related`).then((r) => setRelated(r.data)).catch(() => setRelated({ same_employee: [], same_dept_issue: [], same_high_risk: [] }));
      api.get(`/cases/${id}/timeline?limit=100`).then((r) => setTimeline(r.data)).catch(() => setTimeline({ items: [] }));
      // Phase 10 — witnesses (any case-visible role)
      api
        .get(`/cases/${id}/witnesses`)
        .then((r) => setWitnesses(r.data || []))
        .catch(() => setWitnesses([]));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const specificOptions = useMemo(
    () => taxonomy?.[editForm.issue_type] || [],
    [taxonomy, editForm.issue_type]
  );

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = {};
    for (const [k, v] of Object.entries(editForm)) {
      if (v === "" || v === null || v === undefined) continue;
      body[k] = v;
    }
    // Phase 2 (Feb 2026) — explicit clear support for date_of_discussion:
    // if the user emptied the field but the case previously had a value, we
    // need to send `null` so the backend actually clears it. All other
    // optional fields keep the old "skip if empty" semantics.
    if (
      (editForm.date_of_discussion || "") === "" &&
      caseData?.date_of_discussion
    ) {
      body.date_of_discussion = null;
    }
    try {
      await api.patch(`/cases/${id}`, body);
      toast.success("Case updated");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleReopen = async (reason) => {
    try {
      await api.patch(`/cases/${id}`, {
        status: "In Progress",
        reopen_reason: reason,
      });
      toast.success("Case reopened");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) {
      toast.error("Choose a file");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", uploadFile);
    fd.append("category", uploadCategory);
    if (uploadReplacesId) fd.append("replaces_document_id", uploadReplacesId);
    try {
      await api.post(`/cases/${id}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(uploadReplacesId ? "New version uploaded" : "Document uploaded");
      setUploadFile(null);
      setUploadReplacesId("");
      load();
    } catch (err) {
      // Phase 2 (Jun 2026) — use describeApiError so the toast surfaces
      // structured backend hints (e.g. ``storage_unavailable``) and gateway
      // failures (502/503/504) instead of falling back to a generic
      // "Something went wrong" string. The document upload path can fail at
      // the GCS layer; the admin needs to see *which* layer broke.
      const detail = err?.response?.data?.detail;
      let msg;
      if (detail && typeof detail === "object" && detail.error === "storage_unavailable") {
        msg = `Document storage unavailable (${detail.type}). ${detail.hint || ""}`.trim();
      } else {
        msg = describeApiError(err);
      }
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const token = localStorage.getItem("er_token");
      const res = await fetch(`${API_BASE}/documents/${doc.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        let detail = `Download failed (${res.status})`;
        try {
          const data = await res.json();
          if (data?.detail) detail = typeof data.detail === "string" ? data.detail : JSON.stringify(data.detail);
        } catch {
          /* ignore */
        }
        throw new Error(detail);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Phase 9 — apply PHI override (HR admin only)
  const handlePhiOverride = async (reason) => {
    if (!overrideDoc) return;
    try {
      await api.patch(`/documents/${overrideDoc.id}`, {
        phi_override: true,
        override_reason: reason,
      });
      toast.success("PHI override applied — document is now standard access");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  // Phase 10 — legal hold toggle (HR Admin only)
  const handleLegalHoldToggle = async (placing, reason) => {
    try {
      const body = placing
        ? { legal_hold: true, legal_hold_reason: reason }
        : { legal_hold: false, legal_hold_reason: reason || null };
      await api.patch(`/cases/${id}`, body);
      toast.success(placing ? "Legal hold placed" : "Legal hold released");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  // Phase 2 (May 2026) — admin archive / restore handlers
  const handleArchive = async () => {
    if (archiveConfirmText.trim() !== (caseData?.case_id || "")) return;
    setArchiveSubmitting(true);
    try {
      await api.post(`/cases/${id}/archive`, {
        reason: archiveReason.trim(),
        confirm_case_id: archiveConfirmText.trim(),
      });
      toast.success("Case archived");
      setArchiveOpen(false);
      setArchiveReason("");
      setArchiveConfirmText("");
      load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || err.message,
      );
    } finally {
      setArchiveSubmitting(false);
    }
  };

  const handleRestore = async () => {
    setRestoreSubmitting(true);
    try {
      await api.post(`/cases/${id}/restore`, {
        reason: restoreReason.trim() || null,
      });
      toast.success("Case restored");
      setRestoreOpen(false);
      setRestoreReason("");
      load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || err.message,
      );
    } finally {
      setRestoreSubmitting(false);
    }
  };

  // Phase 2 (May 2026) — hard delete (admin only, refused while on legal hold)
  const handleHardDelete = async () => {
    if (deleteConfirmText.trim() !== (caseData?.case_id || "")) return;
    if (deleteReason.trim().length < 10) return;
    setDeleteSubmitting(true);
    try {
      const { data } = await api.delete(`/cases/${id}`, {
        data: {
          confirm_case_id: deleteConfirmText.trim(),
          reason: deleteReason.trim(),
        },
      });
      const orphanCount = (data?.gcs_orphans || []).length;
      toast.success(
        `Case ${data.deleted_case_id_display} permanently deleted.`
        + (orphanCount > 0 ? ` (${orphanCount} document(s) couldn't be removed from storage and were logged as orphans.)` : ""),
      );
      setDeleteOpen(false);
      navigate("/cases");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || err.message,
      );
    } finally {
      setDeleteSubmitting(false);
    }
  };

  // Phase 10 — witness CRUD
  const handleWitnessSave = async (payload) => {
    try {
      if (editingWitness) {
        await api.patch(`/cases/${id}/witnesses/${editingWitness.id}`, payload);
        toast.success("Witness interview updated");
      } else {
        await api.post(`/cases/${id}/witnesses`, payload);
        toast.success("Witness interview added");
      }
      setEditingWitness(null);
      const r = await api.get(`/cases/${id}/witnesses`);
      setWitnesses(r.data || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  // Phase 10 — compliance checklist (lazy)
  const fetchChecklist = async () => {
    setChecklistLoading(true);
    try {
      const r = await api.get(`/cases/${id}/copilot/compliance-checklist`);
      setChecklist(r.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setChecklistLoading(false);
    }
  };

  // Phase 4c — case PDF chronology download
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const token = localStorage.getItem("er_token");
      const res = await fetch(`${API_BASE}/cases/${id}/export.pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`PDF export failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${caseData?.case_id || "case"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (loading || !caseData) {
    return (
      <div
        data-testid="case-detail-loading"
        className="mx-auto max-w-6xl space-y-4 px-6 py-10"
      >
        <div className="skeleton-shimmer h-6 w-48" />
        <div className="skeleton-shimmer h-24 w-full" />
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="skeleton-shimmer h-96 lg:col-span-2" />
          <div className="skeleton-shimmer h-96" />
        </div>
      </div>
    );
  }

  return (
    <div data-testid="case-detail-page" className="mx-auto max-w-6xl px-6 py-8 space-y-6">
      <ReopenDialog open={reopenOpen} setOpen={setReopenOpen} onConfirm={handleReopen} />
      <PhiOverrideDialog
        open={!!overrideDoc}
        setOpen={(o) => !o && setOverrideDoc(null)}
        doc={overrideDoc}
        onConfirm={handlePhiOverride}
      />
      <LegalHoldDialog
        open={legalHoldOpen}
        setOpen={setLegalHoldOpen}
        currentlyHeld={onLegalHold}
        currentReason={caseData?.legal_hold_reason}
        onConfirm={handleLegalHoldToggle}
      />

      {/* Phase 2 (May 2026) — Archive (soft-delete) confirm dialog */}
      <Dialog
        open={archiveOpen}
        onOpenChange={(o) => {
          if (!archiveSubmitting && !o) setArchiveOpen(false);
        }}
      >
        <DialogContent data-testid="archive-case-dialog">
          <DialogHeader>
            <DialogTitle>Archive this case?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p style={{ color: "var(--page-fg-secondary)" }}>
              Archive hides the case from default lists and disables further edits.
              The case stays fully searchable under the <em>Show archived</em> toggle
              and is restorable by any admin. Audit trail, documents, and witness
              interviews are preserved unchanged.
            </p>
            <div>
              <Label
                className="text-xs uppercase tracking-wider"
                style={{ color: "var(--page-fg-muted)" }}
              >
                Reason for archive (required)
              </Label>
              <Textarea
                value={archiveReason}
                onChange={(e) => setArchiveReason(e.target.value)}
                rows={3}
                maxLength={500}
                data-testid="archive-case-reason"
                placeholder="e.g. Case fully resolved 6 months ago; closing out workspace"
              />
            </div>
            <div>
              <Label
                className="text-xs uppercase tracking-wider"
                style={{ color: "var(--page-fg-muted)" }}
              >
                Type the case ID to confirm:{" "}
                <span className="font-mono" style={{ color: "var(--page-fg)" }}>
                  {caseData?.case_id}
                </span>
              </Label>
              <Input
                value={archiveConfirmText}
                onChange={(e) => setArchiveConfirmText(e.target.value)}
                data-testid="archive-case-confirm-id"
                placeholder={caseData?.case_id}
                autoComplete="off"
                spellCheck={false}
              />
            </div>
          </div>
          <DialogFooter className="mt-3 gap-2">
            <Button
              variant="outline"
              onClick={() => setArchiveOpen(false)}
              disabled={archiveSubmitting}
              data-testid="archive-case-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={handleArchive}
              disabled={
                archiveSubmitting
                || !archiveReason.trim()
                || archiveConfirmText.trim() !== (caseData?.case_id || "")
              }
              data-testid="archive-case-confirm"
            >
              {archiveSubmitting ? "Archiving…" : "Archive case"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 2 (May 2026) — Restore confirm dialog */}
      <Dialog
        open={restoreOpen}
        onOpenChange={(o) => {
          if (!restoreSubmitting && !o) setRestoreOpen(false);
        }}
      >
        <DialogContent data-testid="restore-case-dialog">
          <DialogHeader>
            <DialogTitle>Restore this case?</DialogTitle>
          </DialogHeader>
          <p className="text-sm" style={{ color: "var(--page-fg-secondary)" }}>
            The case will return to the active list and become editable again.
          </p>
          <div>
            <Label
              className="text-xs uppercase tracking-wider"
              style={{ color: "var(--page-fg-muted)" }}
            >
              Reason (optional)
            </Label>
            <Textarea
              value={restoreReason}
              onChange={(e) => setRestoreReason(e.target.value)}
              rows={2}
              maxLength={500}
              data-testid="restore-case-reason"
              placeholder="e.g. New information surfaced; re-opening for review"
            />
          </div>
          <DialogFooter className="mt-3 gap-2">
            <Button
              variant="outline"
              onClick={() => setRestoreOpen(false)}
              disabled={restoreSubmitting}
              data-testid="restore-case-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={handleRestore}
              disabled={restoreSubmitting}
              data-testid="restore-case-confirm"
            >
              {restoreSubmitting ? "Restoring…" : "Restore case"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 2 (May 2026) — Hard-delete case confirm dialog */}
      <Dialog
        open={deleteOpen}
        onOpenChange={(o) => {
          if (!deleteSubmitting && !o) setDeleteOpen(false);
        }}
      >
        <DialogContent data-testid="delete-case-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-[color:var(--danger-fg)]" />
              Permanently delete this case?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div
              className="rounded-md border p-3 text-[12.5px] leading-relaxed"
              style={{
                backgroundColor: "var(--danger-bg)",
                borderColor: "var(--danger-border)",
                color: "var(--page-fg)",
              }}
            >
              <p>
                <strong>⚠️ Permanent deletion.</strong> This case and all attached
                documents will be removed forever. This action cannot be undone.
              </p>
              <p className="mt-1.5">
                Past audit log entries about this case remain immutable but will
                reference a deleted case ID. Use <em>Archive</em> instead if you
                want to hide the case but keep it retrievable.
              </p>
            </div>
            <div>
              <Label
                className="text-xs uppercase tracking-wider"
                style={{ color: "var(--page-fg-muted)" }}
              >
                Type the case ID to confirm:{" "}
                <span className="font-mono" style={{ color: "var(--page-fg)" }}>
                  {caseData?.case_id}
                </span>
              </Label>
              <Input
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                data-testid="delete-case-confirm-id"
                placeholder={caseData?.case_id}
                autoComplete="off"
                spellCheck={false}
              />
            </div>
            <div>
              <Label
                className="text-xs uppercase tracking-wider"
                style={{ color: "var(--page-fg-muted)" }}
              >
                Reason for deletion (required, min 10 chars)
              </Label>
              <Textarea
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                rows={3}
                minLength={10}
                maxLength={2000}
                data-testid="delete-case-reason"
                placeholder="e.g. Privacy-rights deletion request from employee; case fully resolved; legal hold cleared; etc."
              />
              <p className="mt-1 text-[10px] text-[color:var(--page-fg-muted)]">
                Recorded in the audit log alongside the case metadata.
              </p>
            </div>
          </div>
          <DialogFooter className="mt-3 gap-2">
            <Button
              variant="outline"
              onClick={() => setDeleteOpen(false)}
              disabled={deleteSubmitting}
              data-testid="delete-case-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={handleHardDelete}
              disabled={
                deleteSubmitting
                || deleteConfirmText.trim() !== (caseData?.case_id || "")
                || deleteReason.trim().length < 10
              }
              data-testid="delete-case-confirm"
              className="bg-[color:var(--danger-fg)] hover:bg-[color:var(--danger-fg)] hover:opacity-90 text-white"
            >
              {deleteSubmitting ? "Deleting…" : "Delete permanently"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <WitnessDialog
        open={witnessOpen}
        setOpen={(o) => {
          setWitnessOpen(o);
          if (!o) setEditingWitness(null);
        }}
        existing={editingWitness}
        onConfirm={handleWitnessSave}
      />

      <div className="brand-card px-6 py-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <Button
              asChild
              variant="ghost"
              size="sm"
              style={{ color: "var(--page-fg-secondary)" }}
            >
              <Link to="/cases" data-testid="back-to-cases-button">
                <ArrowLeft className="mr-1 h-4 w-4" strokeWidth={1.8} />
                Cases
              </Link>
            </Button>
            <span
              className="rounded-md px-2.5 py-1 font-mono text-sm font-semibold tabular-nums"
              style={{
                backgroundColor: "var(--surface-2)",
                color: "var(--page-fg)",
                border: "1px solid var(--border-subtle)",
              }}
            >
              {caseData.case_id}
            </span>
            <StatusPill status={caseData.status} />
            {caseData.high_risk && <HighRiskBadge />}
            {caseData.imported && (
              <span
                data-testid="imported-from-legacy-badge"
                title={
                  "Imported from legacy system" +
                  (caseData.legacy_case_id ? ` · legacy id: ${caseData.legacy_case_id}` : "") +
                  (caseData.imported_at ? ` · at ${caseData.imported_at}` : "") +
                  (caseData.import_batch_id ? ` · batch ${caseData.import_batch_id}` : "")
                }
                className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.10em]"
                style={{
                  backgroundColor: "var(--surface-2)",
                  color: "var(--page-fg-secondary)",
                  border: "1px dashed var(--border-subtle)",
                }}
              >
                <FileSpreadsheet className="h-3.5 w-3.5" strokeWidth={1.7} />
                Imported · legacy
              </span>
            )}
            {onLegalHold && (
              <span
                data-testid="legal-hold-badge"
                title={caseData.legal_hold_reason || "Legal hold active"}
                className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold uppercase tracking-wide"
                style={{
                  backgroundColor: "rgba(229,77,77,0.16)",
                  color: "#C03030",
                  border: "1px solid rgba(229,77,77,0.5)",
                }}
              >
                <Gavel className="h-3.5 w-3.5" />
                Legal hold
              </span>
            )}
            {archived && (
              <span
                data-testid="archived-badge"
                className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold uppercase tracking-wide"
                style={{
                  backgroundColor: "rgba(100,116,139,0.16)",
                  color: "var(--page-fg-secondary)",
                  border: "1px solid rgba(100,116,139,0.5)",
                }}
              >
                <ArchiveIcon className="h-3.5 w-3.5" />
                Archived
              </span>
            )}
          </div>
          {isHRAdmin && !archived && (
            <Button
              data-testid="legal-hold-toggle-button"
              variant="outline"
              onClick={() => setLegalHoldOpen(true)}
              className={
                onLegalHold
                  ? "border-[color:rgba(214,177,106,0.45)] text-[color:#9A7A2A] dark:text-[color:#E8CB8E] hover:bg-[color:rgba(214,177,106,0.10)]"
                  : "border-[color:var(--danger-border)] text-[color:var(--danger-fg)] hover:bg-[color:var(--danger-bg)]"
              }
            >
              <Gavel className="mr-1 h-4 w-4" />
              {onLegalHold ? "Release legal hold" : "Place on legal hold"}
            </Button>
          )}
          {isHRAdmin && !archived && !onLegalHold && (
            <Button
              data-testid="case-archive-button"
              variant="outline"
              onClick={() => {
                setArchiveReason("");
                setArchiveConfirmText("");
                setArchiveOpen(true);
              }}
              className="border-[color:var(--border-strong)] text-[color:var(--page-fg-secondary)] hover:bg-[color:var(--surface-2)]"
              title="Archive (soft-delete) — hides from default views. Reversible."
            >
              <ArchiveIcon className="mr-1 h-4 w-4" />
              Archive case
            </Button>
          )}
          {isHRAdmin && archived && (
            <Button
              data-testid="case-restore-button"
              variant="outline"
              onClick={() => { setRestoreReason(""); setRestoreOpen(true); }}
              className="border-[color:var(--border-strong)] text-[color:var(--page-fg)] hover:bg-[color:var(--surface-2)]"
              title="Restore — undo archive and return the case to active views."
            >
              <RotateCcw className="mr-1 h-4 w-4" />
              Restore case
            </Button>
          )}
          {/* Phase 2 (May 2026) — Delete permanently. Visually de-emphasised. */}
          {isHRAdmin && !onLegalHold && (
            <button
              type="button"
              data-testid="case-hard-delete-link"
              onClick={() => {
                setDeleteConfirmText("");
                setDeleteReason("");
                setDeleteOpen(true);
              }}
              className="ml-1 text-xs text-[color:var(--page-fg-muted)] underline-offset-2 hover:text-[color:var(--danger-fg)] hover:underline"
              title="Permanently delete this case and all attached documents. Cannot be undone."
            >
              Delete permanently
            </button>
          )}
          {isHR && caseData.status === "Closed" && !archived && (
            <Button
              data-testid="reopen-case-button"
              variant="outline"
              onClick={() => setReopenOpen(true)}
              className="rounded-md border-[color:var(--border-strong)] text-[color:var(--page-fg)] hover:bg-[color:var(--surface-2)]"
            >
              <RotateCcw className="mr-1 h-4 w-4" />
              Reopen case
            </Button>
          )}
          <Button
            data-testid="download-pdf-button"
            variant="outline"
            onClick={handleDownloadPdf}
            disabled={downloadingPdf}
            className="rounded-md border-[color:var(--warning-border)] bg-transparent hover:bg-[color:var(--warning-bg)]"
            style={{ color: "var(--warning-fg)" }}
          >
            <Download className="mr-1 h-4 w-4" />
            {downloadingPdf ? "Generating…" : "Download PDF"}
          </Button>
        </div>
        <div className="mt-4">
          <p
            className="font-serif text-2xl font-semibold tracking-tight"
            style={{ letterSpacing: "-0.015em", color: "var(--page-fg)" }}
          >
            {caseData.team_member_name}
          </p>
          <p className="text-sm tabular-nums" style={{ color: "var(--page-fg-secondary)" }}>
            Position ID {caseData.team_member_position_id || caseData.team_member_file_number} · {caseData.team_member_service_line}
            {caseData.assigned_hr_partner_name && ` · HR: ${caseData.assigned_hr_partner_name}`}
          </p>
        </div>
      </div>

      {!caseData.assigned_hr_partner_id && !caseData.archived && (
        <div
          data-testid="case-unassigned-notice"
          className="mt-3 rounded-lg border px-3 py-2 text-[12.5px]"
          style={{
            borderColor: "var(--border-subtle)",
            backgroundColor: "var(--surface-2)",
            color: "var(--page-fg-secondary)",
          }}
        >
          This case has no HR partner assigned. Assign one to enable HR-partner-based
          email notifications (SLA reminders, follow-up nudges, high-risk alerts).
        </div>
      )}

      <Tabs defaultValue="details" className="w-full" data-testid="case-detail-tabs">
        <TabsList
          className="h-auto gap-1 rounded-none bg-transparent p-0"
          style={{ borderBottom: "1px solid var(--border-subtle)" }}
        >
          <TabsTrigger value="details" className="relative h-9 rounded-none border-b-2 border-transparent bg-transparent px-3 text-sm text-[color:var(--page-fg-secondary)] data-[state=active]:bg-transparent data-[state=active]:text-[color:var(--page-fg)] data-[state=active]:border-[color:var(--page-fg)] data-[state=active]:shadow-none" data-testid="tab-details">Details</TabsTrigger>
          <TabsTrigger value="documents" className="relative h-9 rounded-none border-b-2 border-transparent bg-transparent px-3 text-sm text-[color:var(--page-fg-secondary)] data-[state=active]:bg-transparent data-[state=active]:text-[color:var(--page-fg)] data-[state=active]:border-[color:var(--page-fg)] data-[state=active]:shadow-none" data-testid="tab-documents">
            Documents <Badge variant="secondary" className="ml-2">{documents.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="witnesses" className="relative h-9 rounded-none border-b-2 border-transparent bg-transparent px-3 text-sm text-[color:var(--page-fg-secondary)] data-[state=active]:bg-transparent data-[state=active]:text-[color:var(--page-fg)] data-[state=active]:border-[color:var(--page-fg)] data-[state=active]:shadow-none" data-testid="tab-witnesses">
            Witnesses <Badge variant="secondary" className="ml-2">{witnesses.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="followup" className="relative h-9 rounded-none border-b-2 border-transparent bg-transparent px-3 text-sm text-[color:var(--page-fg-secondary)] data-[state=active]:bg-transparent data-[state=active]:text-[color:var(--page-fg)] data-[state=active]:border-[color:var(--page-fg)] data-[state=active]:shadow-none" data-testid="tab-followup">Follow-up</TabsTrigger>
          <TabsTrigger value="related" className="relative h-9 rounded-none border-b-2 border-transparent bg-transparent px-3 text-sm text-[color:var(--page-fg-secondary)] data-[state=active]:bg-transparent data-[state=active]:text-[color:var(--page-fg)] data-[state=active]:border-[color:var(--page-fg)] data-[state=active]:shadow-none" data-testid="tab-related">
            Related Cases
            {related && (
              <Badge variant="secondary" className="ml-2">
                {related.same_employee.length + related.same_dept_issue.length + related.same_high_risk.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="timeline" className="relative h-9 rounded-none border-b-2 border-transparent bg-transparent px-3 text-sm text-[color:var(--page-fg-secondary)] data-[state=active]:bg-transparent data-[state=active]:text-[color:var(--page-fg)] data-[state=active]:border-[color:var(--page-fg)] data-[state=active]:shadow-none" data-testid="tab-timeline">Timeline</TabsTrigger>
          {canSeeCopilot && (
            <TabsTrigger value="copilot" className="relative h-9 rounded-none border-b-2 border-transparent bg-transparent px-3 text-sm text-[color:var(--page-fg-secondary)] data-[state=active]:bg-transparent data-[state=active]:text-[color:var(--page-fg)] data-[state=active]:border-[color:var(--page-fg)] data-[state=active]:shadow-none" data-testid="tab-copilot">
              <Sparkles className="mr-1 h-3.5 w-3.5" />
              Co-pilot
            </TabsTrigger>
          )}
          {canSeeAudit && (
            <TabsTrigger value="audit" className="relative h-9 rounded-none border-b-2 border-transparent bg-transparent px-3 text-sm text-[color:var(--page-fg-secondary)] data-[state=active]:bg-transparent data-[state=active]:text-[color:var(--page-fg)] data-[state=active]:border-[color:var(--page-fg)] data-[state=active]:shadow-none" data-testid="tab-audit">
              Audit Trail <Badge variant="secondary" className="ml-2">{auditLogs.length}</Badge>
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="details">
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="brand-card lg:col-span-2">
              <div className="border-b border-[color:var(--border-subtle)] px-6 py-4">
                <h2 className="text-base font-semibold text-[color:var(--page-fg)]">Case details</h2>
              </div>
              <div className="px-6 py-2">
                <FieldRow label="Issue type">{caseData.issue_type}</FieldRow>
                <FieldRow label="Specific issue">{caseData.specific_issue}</FieldRow>
                <FieldRow label="Source">{caseData.source}</FieldRow>
                <FieldRow label="Date reported">{formatDate(caseData.date_reported)}</FieldRow>
                <FieldRow label="Date of discussion">
                  {formatDate(caseData.date_of_discussion)}
                </FieldRow>
                <FieldRow label="Reported by">{caseData.reported_by}</FieldRow>
                <FieldRow label="Investigation start">
                  {formatDate(caseData.investigation_start_date)}
                </FieldRow>
                <FieldRow label="Investigation end">
                  {formatDate(caseData.investigation_end_date)}
                </FieldRow>
                <FieldRow label="Outcome">{caseData.outcome}</FieldRow>
                {caseData.status === "Closed" && (
                  <FieldRow label="Closure summary">
                    <p
                      data-testid="case-closure-summary"
                      className="whitespace-pre-wrap rounded-lg p-3"
                      style={{
                        backgroundColor: "rgba(43, 122, 75, 0.18)",
                        color: "#9BD8B0",
                      }}
                    >
                      {caseData.closure_summary || "—"}
                    </p>
                  </FieldRow>
                )}
                <FieldRow label="Case details">
                  {caseData.case_details ? (
                    <p className="whitespace-pre-wrap">{caseData.case_details}</p>
                  ) : (
                    <span className="text-[color:var(--page-fg-muted)]">No details provided</span>
                  )}
                </FieldRow>
                <FieldRow label="Created">
                  {new Date(caseData.created_at).toLocaleString()}
                </FieldRow>
                <FieldRow label="Last updated">
                  {new Date(caseData.updated_at).toLocaleString()}
                </FieldRow>
              </div>
            </div>

            {canEdit && (
              <form
                onSubmit={handleSave}
                data-testid="update-case-form"
                className="brand-card h-fit space-y-4 p-6"
              >
                <h2 className="text-base font-semibold text-[color:var(--page-fg)]">Update case</h2>

                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-[color:var(--page-fg-secondary)]">Status</Label>
                  <Select
                    value={editForm.status}
                    onValueChange={(v) => setEditForm((f) => ({ ...f, status: v }))}
                  >
                    <SelectTrigger data-testid="update-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((s) => (
                        <SelectItem key={s} value={s}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-[color:var(--page-fg-secondary)]">Issue type</Label>
                  <Select
                    value={editForm.issue_type}
                    onValueChange={(v) =>
                      setEditForm((f) => ({
                        ...f,
                        issue_type: v,
                        specific_issue: (taxonomy?.[v] || [])[0] || "",
                      }))
                    }
                  >
                    <SelectTrigger data-testid="update-issue-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.keys(taxonomy).map((t) => (
                        <SelectItem key={t} value={t}>
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-[color:var(--page-fg-secondary)]">Specific issue</Label>
                  {specificOptions.length > 0 ? (
                    <Select
                      value={editForm.specific_issue}
                      onValueChange={(v) => setEditForm((f) => ({ ...f, specific_issue: v }))}
                    >
                      <SelectTrigger data-testid="update-specific-issue-select">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {specificOptions.map((s) => (
                          <SelectItem key={s} value={s}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <>
                      <Input
                        data-testid="update-specific-issue-input"
                        value={editForm.specific_issue || ""}
                        onChange={(e) => setEditForm((f) => ({ ...f, specific_issue: e.target.value }))}
                        placeholder="Describe the specific issue"
                      />
                      <p className="text-[11px] text-[color:var(--page-fg-muted)]">
                        No subcategories defined for this issue type — enter freely.
                      </p>
                    </>
                  )}
                </div>

                {/* Feb 2026 — case_details is the free-text narrative; HR admins
                    frequently fix typos or add context after intake / CSV
                    import. Same permission gate as other case edit fields
                    (canEdit). Backend already accepts case_details via
                    CaseUpdate and the audit log captures before/after via the
                    standard diff_before_after helper. */}
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-[color:var(--page-fg-secondary)]">
                    Case details
                  </Label>
                  <Textarea
                    rows={4}
                    value={editForm.case_details || ""}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, case_details: e.target.value }))
                    }
                    placeholder="Narrative describing what happened, who was involved, and the initial context."
                    data-testid="update-case-details-input"
                    aria-label="Case details narrative"
                    className="resize-y min-h-[88px]"
                  />
                  <p className="text-[11px] text-[color:var(--page-fg-muted)]">
                    Free-text narrative. Edits are audit-logged with a before/after diff.
                  </p>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-[color:var(--page-fg-secondary)]">
                    Investigation start
                  </Label>
                  <Input
                    type="date"
                    value={editForm.investigation_start_date}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, investigation_start_date: e.target.value }))
                    }
                    data-testid="update-inv-start-input"
                    aria-label="Investigation start date"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-[color:var(--page-fg-secondary)]">
                    Date of discussion
                  </Label>
                  <Input
                    type="date"
                    value={editForm.date_of_discussion}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, date_of_discussion: e.target.value }))
                    }
                    data-testid="update-date-of-discussion-input"
                    aria-label="Date HR met with the employee"
                  />
                  <p className="text-[11px] text-[color:var(--page-fg-muted)]">
                    When HR met with the employee. Clear the field to remove.
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-[color:var(--page-fg-secondary)]">
                    Investigation end (closure date)
                  </Label>
                  <Input
                    type="date"
                    value={editForm.investigation_end_date}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, investigation_end_date: e.target.value }))
                    }
                    data-testid="update-inv-end-input"
                    aria-label="Investigation end date (drives time-to-resolution)"
                  />
                  <p className="text-[11px] text-[color:var(--page-fg-muted)]">
                    Closure timestamp. Drives dashboard <strong>time-to-resolution</strong>{" "}
                    (<code>investigation_end_date − date_reported</code>). Edits are
                    audit-logged. Must be on or after Date reported and no more
                    than 7 days in the future.
                  </p>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-[color:var(--page-fg-secondary)]">Outcome</Label>
                  <Input
                    value={editForm.outcome}
                    onChange={(e) => setEditForm((f) => ({ ...f, outcome: e.target.value }))}
                    placeholder="e.g. Written warning issued"
                    data-testid="update-outcome-input"
                  />
                </div>

                {editForm.status === "Closed" && (
                  <div className="space-y-1.5" data-testid="closure-summary-block">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs font-medium text-[color:var(--page-fg-secondary)]">
                        Closure summary <span style={{ color: "#FCA5A8" }}>*</span>
                      </Label>
                      {isHR && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-7 px-2.5 text-[11px]"
                          disabled={draftingClosure}
                          data-testid="closure-draft-btn"
                          onClick={async () => {
                            setDraftingClosure(true);
                            try {
                              const { data } = await api.post(
                                `/cases/${id}/copilot/closure-draft`,
                              );
                              const draft = (data?.draft || "").trim();
                              if (!draft) {
                                toast.error("Could not generate a draft");
                                return;
                              }
                              const existing = (editForm.closure_summary || "").trim();
                              let next = draft;
                              if (existing) {
                                const append = window.confirm(
                                  "Replace existing draft? Click Cancel to append instead.",
                                );
                                next = append
                                  ? draft
                                  : `${existing}\n\n${draft}`;
                              }
                              setEditForm((f) => ({ ...f, closure_summary: next }));
                              toast.success(
                                `AI draft inserted (${data.char_count} chars). ${
                                  data.disclaimer || ""
                                }`,
                              );
                            } catch (e) {
                              toast.error("Closure draft failed");
                            } finally {
                              setDraftingClosure(false);
                            }
                          }}
                        >
                          <Sparkles className="mr-1 h-3 w-3" />
                          {draftingClosure ? "Drafting…" : "Draft closure summary"}
                        </Button>
                      )}
                    </div>
                    <DictationField
                      rows={3}
                      value={editForm.closure_summary || ""}
                      onChange={(e) =>
                        setEditForm((f) => ({ ...f, closure_summary: e.target.value }))
                      }
                      placeholder="What was decided, communicated, and documented when this case closed?"
                      data-testid="update-closure-summary-textarea"
                      testidPrefix="update-closure-summary"
                      targetField="closure_summary"
                      required
                    />
                    <p className="text-[11px] text-[color:var(--page-fg-secondary)]">
                      Required to transition a case to Closed.
                    </p>
                  </div>
                )}

                {isHR && (
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium text-[color:var(--page-fg-secondary)]">Source</Label>
                    <Select
                      value={editForm.source}
                      onValueChange={(v) => setEditForm((f) => ({ ...f, source: v }))}
                    >
                      <SelectTrigger data-testid="update-source-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CASE_SOURCES.map((s) => (
                          <SelectItem key={s} value={s}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {isHR && (
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium text-[color:var(--page-fg-secondary)]">
                      Date reported
                    </Label>
                    <Input
                      type="date"
                      value={editForm.date_reported || ""}
                      onChange={(e) =>
                        setEditForm((f) => ({ ...f, date_reported: e.target.value }))
                      }
                      data-testid="update-date-reported-input"
                      aria-label="Date the case was originally reported"
                    />
                    <p className="text-[11px] text-[color:var(--page-fg-muted)]">
                      Original intake date. Correct here after CSV import if needed.
                    </p>
                  </div>
                )}

                {isHR && (
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium text-[color:var(--page-fg-secondary)]">
                      Reported by
                    </Label>
                    <Input
                      value={editForm.reported_by || ""}
                      onChange={(e) =>
                        setEditForm((f) => ({ ...f, reported_by: e.target.value }))
                      }
                      placeholder="Name of the person who reported the issue"
                      data-testid="update-reported-by-input"
                      aria-label="Name of the person who originally reported the case"
                    />
                  </div>
                )}

                {isHR && hrPartners.length > 0 && (
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium text-[color:var(--page-fg-secondary)]">
                      Assigned HR partner
                    </Label>
                    <Select
                      value={editForm.assigned_hr_partner_id || ""}
                      onValueChange={(v) =>
                        setEditForm((f) => ({ ...f, assigned_hr_partner_id: v }))
                      }
                    >
                      <SelectTrigger data-testid="update-assigned-partner-select">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {hrPartners.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.full_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={saving}
                  data-testid="update-case-submit-button"
                  className="w-full bg-brand-teal text-white hover:bg-brand-tealDark"
                >
                  {saving ? "Saving…" : "Save changes"}
                </Button>
              </form>
            )}
          </div>
        </TabsContent>

        <TabsContent value="documents">
          <div className="brand-card p-6">
            {/* Phase 9 — HIPAA banner when any document is PHI-restricted */}
            {documents.some((d) => d.access_level === "restricted") && (
              <div
                data-testid="phi-restricted-banner"
                className="mb-4 flex items-start gap-3 rounded-xl px-4 py-3"
                style={{
                  backgroundColor: "rgba(232, 203, 142, 0.16)",
                  border: "1px solid rgba(232, 203, 142, 0.5)",
                }}
              >
                <ShieldAlert className="mt-0.5 h-5 w-5" style={{ color: "#B58A1A" }} />
                <div className="flex-1 text-xs">
                  <p className="font-semibold text-[color:var(--page-fg)]">
                    Some documents on this case are flagged as containing PHI
                  </p>
                  <p className="mt-0.5 text-[color:var(--page-fg-secondary)]">
                    Restricted documents can only be downloaded by HR admins and Compliance.
                    {canOverridePhi
                      ? " You may override the flag if it is a false positive."
                      : ""}
                  </p>
                </div>
              </div>
            )}
            {canUpload && (
              <form
                onSubmit={handleUpload}
                data-testid="upload-form"
                className="mb-6 flex flex-wrap items-end gap-3 rounded-xl bg-[color:var(--surface-2)] p-4"
              >
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-[color:var(--page-fg-secondary)]">Category</Label>
                  <Select value={uploadCategory} onValueChange={setUploadCategory}>
                    <SelectTrigger className="w-56" data-testid="upload-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DOC_CATEGORIES.map((c) => (
                        <SelectItem key={c} value={c}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-[color:var(--page-fg-secondary)]">
                    Replaces (optional)
                  </Label>
                  <Select
                    value={uploadReplacesId || "none"}
                    onValueChange={(v) => setUploadReplacesId(v === "none" ? "" : v)}
                  >
                    <SelectTrigger className="w-64" data-testid="upload-replaces-select">
                      <SelectValue placeholder="New document" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— New document —</SelectItem>
                      {documents
                        .filter((d) => !d.superseded_by_id)
                        .map((d) => (
                          <SelectItem key={d.id} value={d.id}>
                            v{d.version} · {d.filename}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-[color:var(--page-fg-secondary)]">File</Label>
                  <Input
                    type="file"
                    onChange={async (e) => {
                      const f = e.target.files?.[0] || null;
                      setUploadFile(f);
                      setUploadCategoryConfidence(null);
                      // Phase 5: suggest a category from the filename
                      if (f) {
                        try {
                          const { data } = await api.post(
                            "/documents/suggest-category",
                            { filename: f.name }
                          );
                          if (data?.category) {
                            setUploadCategory(data.category);
                            setUploadCategoryConfidence(data.confidence);
                          }
                        } catch {
                          /* ignore — keep current selection */
                        }
                      }
                    }}
                    data-testid="upload-file-input"
                  />
                  {uploadCategoryConfidence && (
                    <p
                      data-testid="upload-category-suggestion"
                      className="text-[11px]"
                      style={{
                        color:
                          uploadCategoryConfidence === "high"
                            ? "#5EE0E0"
                            : uploadCategoryConfidence === "medium"
                              ? "#E8CB8E"
                              : "#B5B5C2",
                      }}
                    >
                      Auto-suggested category: <b>{uploadCategory}</b> · confidence {uploadCategoryConfidence}
                    </p>
                  )}
                </div>
                <Button
                  type="submit"
                  disabled={uploading}
                  data-testid="upload-submit-button"
                  className="bg-brand-teal text-white hover:bg-brand-tealDark"
                >
                  <UploadIcon className="mr-1 h-4 w-4" />
                  {uploading ? "Uploading…" : uploadReplacesId ? "Upload new version" : "Upload"}
                </Button>
              </form>
            )}

            {documents.length > 0 && documents.some((d) => d.superseded_by_id) && (
              <div className="mb-3 flex items-center justify-end">
                <button
                  type="button"
                  data-testid="toggle-version-history-button"
                  onClick={() => setShowVersionHistory((v) => !v)}
                  className="text-xs font-medium text-ring-teal hover:underline"
                >
                  {showVersionHistory
                    ? "Hide version history"
                    : `Show version history (${documents.filter((d) => d.superseded_by_id).length} superseded)`}
                </button>
              </div>
            )}

            <Table data-testid="documents-table">
              <TableHeader>
                <TableRow className="brand-table-header border-b-brand-ivoryDark hover:bg-transparent">
                  <TableHead>Filename</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Version</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Uploaded</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {documents.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="py-12 text-center text-sm text-[color:var(--page-fg-muted)]">
                      No documents uploaded yet.
                    </TableCell>
                  </TableRow>
                )}
                {documents
                  .filter((d) => showVersionHistory || !d.superseded_by_id)
                  .map((d) => {
                    const supersededByDoc = d.superseded_by_id
                      ? documents.find((x) => x.id === d.superseded_by_id)
                      : null;
                    return (
                      <TableRow
                        key={d.id}
                        data-testid={`document-row-${d.id}`}
                        className={`hover:bg-[color:var(--surface-2)] ${
                          d.superseded_by_id ? "opacity-60" : ""
                        }`}
                      >
                        <TableCell className="font-medium text-[color:var(--page-fg)]">
                          <div className="flex flex-wrap items-center gap-2">
                            <span>{d.filename}</span>
                            {d.access_level === "restricted" && (
                              <span
                                data-testid={`phi-restricted-badge-${d.id}`}
                                title={
                                  d.phi_scan?.categories?.length
                                    ? `PHI categories: ${d.phi_scan.categories.join(", ")}`
                                    : "Flagged as containing PHI"
                                }
                                className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                                style={{
                                  backgroundColor: "rgba(229, 77, 77, 0.16)",
                                  color: "#C03030",
                                  border: "1px solid rgba(229, 77, 77, 0.5)",
                                }}
                              >
                                <Lock className="h-3 w-3" />
                                Restricted (PHI)
                              </span>
                            )}
                            {d.phi_override && (
                              <span
                                data-testid={`phi-override-badge-${d.id}`}
                                title={d.phi_override_reason || ""}
                                className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                                style={{
                                  backgroundColor: "rgba(232, 203, 142, 0.18)",
                                  color: "#7A5C18",
                                  border: "1px solid rgba(232, 203, 142, 0.5)",
                                }}
                              >
                                <Unlock className="h-3 w-3" />
                                PHI overridden
                              </span>
                            )}
                          </div>
                          {supersededByDoc && (
                            <p
                              className="mt-0.5 text-[11px] italic"
                              style={{ color: "#FFA563" }}
                              data-testid={`document-superseded-note-${d.id}`}
                            >
                              Superseded by {supersededByDoc.filename} on{" "}
                              {new Date(supersededByDoc.uploaded_at).toLocaleDateString()}
                            </p>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className="inline-flex items-center rounded-full bg-[color:var(--surface-2)] px-2.5 py-0.5 text-xs font-medium text-[color:var(--page-fg)]">
                            {d.category}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm">
                          <span
                            className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold"
                            style={{
                              backgroundColor: d.superseded_by_id
                                ? "rgba(245, 245, 247, 0.08)"
                                : "rgba(20, 184, 184, 0.18)",
                              color: d.superseded_by_id ? "#B5B5C2" : "#5EE0E0",
                            }}
                          >
                            v{d.version}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm">{(d.size / 1024).toFixed(1)} KB</TableCell>
                        <TableCell className="text-sm text-[color:var(--page-fg-muted)]">
                          {new Date(d.uploaded_at).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex flex-wrap items-center justify-end gap-2">
                            {canOverridePhi && d.access_level === "restricted" && !d.phi_override && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setOverrideDoc(d)}
                                data-testid={`phi-override-button-${d.id}`}
                                className="border-[color:rgba(214,177,106,0.40)] text-[color:#9A7A2A] dark:text-[color:#E8CB8E] hover:bg-[color:rgba(214,177,106,0.10)] hover:text-[color:#9A7A2A] dark:text-[color:#E8CB8E]"
                              >
                                <Unlock className="mr-1 h-3.5 w-3.5" />
                                Override PHI
                              </Button>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDownload(d)}
                              data-testid={`download-document-${d.id}`}
                            >
                              <Download className="mr-1 h-3.5 w-3.5" />
                              Download
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="witnesses">
          <div className="brand-card p-6" data-testid="witnesses-panel">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-base font-semibold text-[color:var(--page-fg)] flex items-center gap-2">
                  <UsersIcon className="h-4 w-4" />
                  Witness interviews
                </h2>
                <p className="mt-1 text-sm text-[color:var(--page-fg-muted)]">
                  First-class record of every interview conducted on this case.
                  Confidential interviews appear redacted in non-Compliance / non-Legal exports.
                </p>
              </div>
              {canEdit && !writesBlocked && (
                <Button
                  data-testid="add-witness-button"
                  onClick={() => {
                    setEditingWitness(null);
                    setWitnessOpen(true);
                  }}
                  className="bg-brand-teal text-white hover:bg-brand-tealDark"
                >
                  <Plus className="mr-1 h-4 w-4" />
                  Add interview
                </Button>
              )}
            </div>

            {writesBlocked && canEdit && (
              <div
                className="mt-4 rounded-xl px-3 py-2 text-xs"
                style={{
                  backgroundColor: "rgba(229,77,77,0.10)",
                  border: "1px solid rgba(229,77,77,0.4)",
                  color: "#7A1A1A",
                }}
              >
                {onLegalHold
                  ? "Witness changes are blocked while this case is on legal hold."
                  : "This case is archived — witness records are read-only."}
              </div>
            )}

            <div className="mt-4 space-y-3">
              {witnesses.length === 0 ? (
                <p
                  className="rounded-xl border border-dashed border-[color:var(--border-subtle)] px-4 py-6 text-center text-sm text-[color:var(--page-fg-muted)]"
                  data-testid="witnesses-empty"
                >
                  No witness interviews on file yet.
                </p>
              ) : (
                witnesses.map((w) => (
                  <div
                    key={w.id}
                    data-testid={`witness-row-${w.id}`}
                    className="rounded-xl border border-[color:var(--border-subtle)] p-4 transition-shadow hover:shadow-brand-sm"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-[color:var(--page-fg)]">
                        {w.interviewee_name}
                      </span>
                      <span className="text-xs text-[color:var(--page-fg-muted)]">
                        {w.interviewee_relationship}
                      </span>
                      {w.confidential && (
                        <span
                          data-testid={`witness-confidential-badge-${w.id}`}
                          className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                          style={{
                            backgroundColor: "rgba(232,203,142,0.18)",
                            color: "#7A5C18",
                            border: "1px solid rgba(232,203,142,0.5)",
                          }}
                        >
                          <LockIcon className="h-3 w-3" /> Confidential
                        </span>
                      )}
                      {w.recorded && (
                        <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide bg-[color:var(--surface-2)] text-brand-tealDark border border-brand-teal/40">
                          Recorded
                        </span>
                      )}
                      {w.redacted && (
                        <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide bg-[color:var(--surface-2)] text-[color:var(--page-fg-secondary)] border border-[color:var(--border-strong)]">
                          Redacted
                        </span>
                      )}
                      <span className="ml-auto text-[11px] text-[color:var(--page-fg-muted)]">
                        {new Date(w.interview_date).toLocaleString()}
                      </span>
                    </div>
                    {w.present_parties?.length > 0 && (
                      <p className="mt-1.5 text-[11px] text-[color:var(--page-fg-muted)]">
                        Present: {w.present_parties.join(" · ")}
                      </p>
                    )}
                    <p className="mt-2 whitespace-pre-line text-sm text-[color:var(--page-fg-secondary)]">
                      {w.summary}
                    </p>
                    {canEdit && !writesBlocked && !w.redacted && (
                      <div className="mt-3 flex justify-end">
                        <Button
                          variant="outline"
                          size="sm"
                          data-testid={`witness-edit-${w.id}`}
                          onClick={() => {
                            setEditingWitness(w);
                            setWitnessOpen(true);
                          }}
                        >
                          <Pencil className="mr-1 h-3.5 w-3.5" />
                          Edit
                        </Button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </TabsContent>



        <TabsContent value="followup">
          <div className="brand-card p-6">
            <h2 className="text-base font-semibold text-[color:var(--page-fg)]">Follow-up tracking</h2>
            <p className="mt-1 text-sm text-[color:var(--page-fg-muted)]">
              Required follow-ups create notifications for the assigned HR partner when due
              within 3 days.
            </p>
            {canEdit ? (
              <div className="mt-6 space-y-4">
                <div className="flex items-center justify-between rounded-xl border border-[color:var(--border-subtle)] p-4">
                  <div>
                    <p className="text-sm font-medium text-[color:var(--page-fg)]">Follow-up required</p>
                    <p className="text-xs text-[color:var(--page-fg-muted)]">
                      Turn on to schedule a follow-up date and notes.
                    </p>
                  </div>
                  <Switch
                    checked={!!editForm.follow_up_required}
                    onCheckedChange={(v) =>
                      setEditForm((f) => ({ ...f, follow_up_required: v }))
                    }
                    data-testid="followup-required-switch"
                  />
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium text-[color:var(--page-fg-secondary)]">Follow-up date</Label>
                    <Input
                      type="date"
                      value={editForm.follow_up_date || ""}
                      onChange={(e) =>
                        setEditForm((f) => ({ ...f, follow_up_date: e.target.value }))
                      }
                      data-testid="followup-date-input"
                      disabled={!editForm.follow_up_required}
                      aria-label="Follow-up date"
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-[color:var(--page-fg-secondary)]">Follow-up notes</Label>
                  <DictationField
                    rows={3}
                    value={editForm.follow_up_notes || ""}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, follow_up_notes: e.target.value }))
                    }
                    data-testid="followup-notes-textarea"
                    testidPrefix="followup-notes"
                    targetField="follow_up_notes"
                    disabled={!editForm.follow_up_required}
                  />
                </div>
                <div
                  className="flex items-center justify-between rounded-xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-3"
                  data-testid="follow-up-completed-block"
                >
                  <div>
                    <Label className="text-sm font-medium text-[color:var(--page-fg)]">
                      Follow-up completed
                    </Label>
                    <p className="text-xs text-[color:var(--page-fg-muted)]">
                      Mark when the planned follow-up action has been delivered.
                    </p>
                  </div>
                  <Switch
                    checked={!!editForm.follow_up_completed}
                    onCheckedChange={(v) =>
                      setEditForm((f) => ({ ...f, follow_up_completed: v }))
                    }
                    disabled={!editForm.follow_up_required}
                    data-testid="follow-up-completed-switch"
                  />
                </div>
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  data-testid="followup-save-button"
                  className="bg-brand-teal text-white hover:bg-brand-tealDark"
                >
                  Save follow-up
                </Button>
              </div>
            ) : (
              <div className="mt-6 space-y-2 text-sm">
                <p>
                  <span className="font-medium">Required:</span>{" "}
                  {caseData.follow_up_required ? "Yes" : "No"}
                </p>
                <p>
                  <span className="font-medium">Due:</span>{" "}
                  {formatDate(caseData.follow_up_date)}
                </p>
                <p>
                  <span className="font-medium">Completed:</span>{" "}
                  {caseData.follow_up_completed ? "Yes" : "No"}
                </p>
                <p className="whitespace-pre-wrap">
                  <span className="font-medium">Notes:</span>{" "}
                  {caseData.follow_up_notes || "—"}
                </p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Phase 5 — Related Cases */}
        <TabsContent value="related">
          <div className="brand-card p-6">
            <h2 className="text-base font-semibold text-[color:var(--page-fg)]">Related cases</h2>
            <p className="mt-1 text-xs text-[color:var(--page-fg-muted)]">
              Heuristic match — same employee · same department + issue · same service-line high-risk
            </p>
            {!related ? (
              <div className="mt-4 h-24 animate-pulse rounded-xl bg-[color:var(--surface-2)]" />
            ) : (
              <div className="mt-4 space-y-6" data-testid="related-cases-content">
                {[
                  ["same_employee", "Same employee · last 180 days"],
                  ["same_dept_issue", "Same department + issue type · last 60 days"],
                  ["same_high_risk", "Same service line · high-risk · last 90 days"],
                ].map(([key, label]) => {
                  const items = related[key] || [];
                  return (
                    <div key={key} data-testid={`related-section-${key}`}>
                      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[color:var(--page-fg-secondary)]">
                        {label} · {items.length}
                      </p>
                      {items.length === 0 ? (
                        <div
                          className="rounded-xl border border-dashed border-[color:var(--border-subtle)] px-3 py-4 text-center text-[11px] text-[color:var(--page-fg-secondary)]"
                          style={{ backgroundColor: "rgba(21, 21, 29, 0.4)" }}
                        >
                          No matches.
                        </div>
                      ) : (
                        <ul className="space-y-1">
                          {items.map((c) => (
                            <li
                              key={c.id}
                              data-testid={`related-row-${c.case_id}`}
                              className="flex items-center gap-3 rounded-xl border border-[color:var(--border-subtle)] px-3 py-2"
                              style={{ backgroundColor: "#15151D" }}
                            >
                              <span className="font-mono text-xs text-ring-teal">{c.case_id}</span>
                              <StatusPill status={c.status} />
                              <span className="flex-1 truncate text-sm text-white">
                                {c.issue_type} — {c.specific_issue}
                                {c.high_risk && (
                                  <span className="ml-2 inline-block rounded-full bg-ring-red/20 px-1.5 py-0.5 text-[10px] font-semibold text-ring-red">
                                    HIGH-RISK
                                  </span>
                                )}
                              </span>
                              <span className="text-[11px] text-[color:var(--page-fg-secondary)]">
                                {formatDate(c.date_reported)}
                              </span>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => navigate(`/cases/${c.id}`)}
                                data-testid={`related-open-${c.case_id}`}
                                className="h-7 rounded-lg border-[color:var(--border-subtle)] text-[color:var(--page-fg-secondary)] hover:border-ring-teal hover:text-ring-teal"
                              >
                                Open
                              </Button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Phase 5 — Timeline */}
        <TabsContent value="timeline">
          <div className="brand-card p-6">
            <h2 className="text-base font-semibold text-[color:var(--page-fg)]">Case timeline</h2>
            <p className="mt-1 text-xs text-[color:var(--page-fg-muted)]">
              Merged feed: status changes · document uploads · field updates · notifications · system events
            </p>
            {!timeline ? (
              <div className="mt-4 h-32 animate-pulse rounded-xl bg-[color:var(--surface-2)]" />
            ) : (timeline.items || []).length === 0 ? (
              <div className="mt-4 rounded-xl border border-dashed border-[color:var(--border-subtle)] px-3 py-8 text-center text-sm text-[color:var(--page-fg-secondary)]">
                No timeline events yet.
              </div>
            ) : (
              <ol
                data-testid="timeline-feed"
                className="relative mt-5 space-y-3 border-l-2 pl-6"
                style={{ borderColor: "#2A2A38" }}
              >
                {timeline.items.map((e) => {
                  const dot =
                    e.color === "blue" ? "#3B82F6"
                      : e.color === "lime" ? "#BDEE63"
                        : e.color === "purple" ? "#8B5CF6"
                          : e.color === "red" ? "#E5484D"
                            : e.color === "magenta" ? "#E12AFB"
                              : "#14B8B8";
                  return (
                    <li
                      key={e.id}
                      data-testid={`timeline-event-${e.id}`}
                      className="relative"
                    >
                      <span
                        className="absolute -left-[34px] top-2 flex h-4 w-4 items-center justify-center rounded-full"
                        style={{ backgroundColor: dot, boxShadow: `0 0 8px ${dot}` }}
                      />
                      <div
                        className="rounded-xl border border-[color:var(--border-subtle)] p-3"
                        style={{ backgroundColor: "#15151D" }}
                      >
                        <div className="flex items-baseline justify-between gap-3">
                          <p className="text-sm font-semibold text-white">
                            {e.title}
                          </p>
                          <span className="text-[11px] text-[color:var(--page-fg-secondary)]">
                            {(e.timestamp || "").slice(0, 19).replace("T", " ")}
                          </span>
                        </div>
                        {e.body && (
                          <p className="mt-1 whitespace-pre-wrap text-xs text-[color:var(--page-fg-secondary)]">
                            {e.body}
                          </p>
                        )}
                        <p className="mt-1 text-[11px]" style={{ color: dot }}>
                          {(e.actor && e.actor.name) || "—"} · {e.kind}
                        </p>
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
        </TabsContent>



        {canSeeCopilot && (
          <TabsContent value="copilot" data-testid="tab-content-copilot">
            <ComplianceChecklistPanel
              data={checklist}
              loading={checklistLoading}
              onLoad={fetchChecklist}
            />
            <CopilotPanel
              caseId={id}
              currentCase={caseData}
              isReadOnly={copilotReadOnly}
              onCaseRefreshed={load}
            />
          </TabsContent>
        )}

        {canSeeAudit && (
          <TabsContent value="audit">
            <div className="brand-card p-6">
              <h2 className="text-base font-semibold text-[color:var(--page-fg)]">Audit trail</h2>
              <Table data-testid="case-audit-table" className="mt-4">
                <TableHeader>
                  <TableRow className="brand-table-header border-b-brand-ivoryDark hover:bg-transparent">
                    <TableHead>When</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Before</TableHead>
                    <TableHead>After</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {auditLogs.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={4}
                        className="py-8 text-center text-sm text-[color:var(--page-fg-muted)]"
                      >
                        No audit entries yet.
                      </TableCell>
                    </TableRow>
                  )}
                  {auditLogs.map((a) => (
                    <TableRow key={a.id} className="hover:bg-[color:var(--surface-2)]">
                      <TableCell className="whitespace-nowrap text-xs text-[color:var(--page-fg-muted)]">
                        {new Date(a.timestamp).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <span className="inline-flex items-center rounded-full bg-[color:var(--surface-2)] px-2.5 py-0.5 text-xs font-medium text-[color:var(--page-fg)]">
                          {a.action}
                        </span>
                      </TableCell>
                      <TableCell>
                        <pre className="max-h-32 max-w-sm overflow-auto text-[11px] text-[color:var(--page-fg-secondary)]">
                          {a.before ? JSON.stringify(a.before, null, 2) : "—"}
                        </pre>
                      </TableCell>
                      <TableCell>
                        <pre className="max-h-32 max-w-sm overflow-auto text-[11px] text-[color:var(--page-fg-secondary)]">
                          {a.after ? JSON.stringify(a.after, null, 2) : "—"}
                        </pre>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
